import { useState, type Dispatch, type SetStateAction } from 'react';

/**
 * An editor's draft of one record that someone else may save at the same time (review W3, W7). The draft remembers the
 * version it started from and sends that, so a colleague's save is never overwritten silently: the server refuses (409).
 * When the record changes underneath:
 * - nothing typed here yet: the editor simply shows their version;
 * - only parts this editor doesn't edit changed (a template certified, approved or rejected): taken in quietly, the draft kept;
 * - they saved exactly what is typed here (our own save coming back): nothing to do;
 * - anything else: `isStale`. Save waits, and "Reload theirs" shows their version instead of this draft.
 */
export interface ServerDraft<TView, TDraft> {
  readonly draft: TDraft;
  readonly setDraft: Dispatch<SetStateAction<TDraft>>;
  /** The record the draft started from; its `version` is what Save sends. */
  readonly base: TView | null;
  readonly isDirty: boolean;
  readonly isStale: boolean;
  /** Throws this draft away for the record as it is now. */
  readonly reload: () => void;
  /** Back to the record the draft started from. */
  readonly discard: () => void;
  /** After a save: `saved` is the new base, and what was sent is no longer a change. */
  readonly adopt: (saved: TView, sent: TDraft) => void;
}

export function useServerDraft<TView extends { readonly version: number }, TDraft>(options: {
  readonly latest: TView | null;
  readonly draftOf: (view: TView | null) => TDraft;
  readonly isSameDraft: (left: TDraft, right: TDraft) => boolean;
  /** The parts of the record this editor edits are the same in both. */
  readonly isSameEditable: (left: TView, right: TView) => boolean;
}): ServerDraft<TView, TDraft> {
  const { latest, draftOf, isSameDraft, isSameEditable } = options;
  const [start] = useState(() => draftOf(latest));
  const [base, setBase] = useState<TView | null>(latest);
  const [baseDraft, setBaseDraft] = useState<TDraft>(start);
  const [draft, setDraft] = useState<TDraft>(start);
  const isDirty = !isSameDraft(draft, baseDraft);

  const follow = (view: TView | null): void => {
    const next = draftOf(view);
    setBase(view);
    setBaseDraft(next);
    setDraft(next);
  };

  // Adjusting state while rendering (React's documented pattern for "state from a changed prop"): the conditions
  // below are false again once applied, so this settles in one extra render.
  // Only a newer version counts: a refetch that started before our own save can land after it with the older one.
  let isStale = false;
  if (latest && base && latest.version > base.version) {
    if (!isDirty) {
      follow(latest);
    } else if (isSameEditable(latest, base)) {
      setBase(latest);
    } else if (isSameDraft(draftOf(latest), draft)) {
      setBase(latest);
      setBaseDraft(draft);
    } else {
      isStale = true;
    }
  }

  return {
    draft,
    setDraft,
    base,
    isDirty,
    isStale,
    reload: () => follow(latest),
    discard: () => setDraft(baseDraft),
    adopt: (saved, sent) => {
      setBase(saved);
      setBaseDraft(sent);
    },
  };
}
